import { CommonModule } from '@angular/common';
import {
	AfterViewInit,
	ChangeDetectionStrategy,
	Component,
	HostListener,
	OnDestroy,
	ViewChild
} from '@angular/core';
import { Router } from '@angular/router';
import { combineLatest, Subject, takeUntil } from 'rxjs';
import {
	AiAssistantComponent,
	AiSearchProgressState,
	AnaEntityReference,
	IconComponent,
	TypographyComponent
} from 'components';
import { LoButton, LoIcon, LoTooltip } from 'lo-design-system';
import { AnaChatHostComponent } from '@brand-lolly/components/ana-chat-host/ana-chat-host.component';
import { AnaCampaignStartComponent } from '@brand-lolly/components/ana-campaign-start/ana-campaign-start.component';
import { AnaAssistantFacadeService } from '@brand-lolly/app/_services/ana-assistant-facade.service';
import { SocialMediaPlatformsEnum } from '@brand-lolly/app/_models/platforms';
import {
	DiscoveryProfileSlider,
	DiscoveryProfileSliderData
} from '@brand-lolly/app/social-media-influencer/modules/discovery-v2/components/discovery-profile-slider/discovery-profile.slider';
import { AnaSearchProgressComponent } from '@brand-lolly/app/shared/ana-assistant-panel/ana-search-progress.component';
import {
	AnaMarketplaceSearchEnd,
	AnaMarketplaceSearchWatchComponent
} from '@brand-lolly/app/shared/ana-assistant-panel/ana-marketplace-search-watch.component';
import {
	AnaFinderSearchEnd,
	AnaFinderSearchWatchComponent
} from '@brand-lolly/app/shared/ana-assistant-panel/ana-finder-search-watch.component';

/** The platforms a discovery profile exists for. An Ana entity can name a channel
 *  (gmail, twitter) that has no profile behind it. */
const DISCOVERY_PLATFORMS: readonly string[] = [
	SocialMediaPlatformsEnum.INSTAGRAM,
	SocialMediaPlatformsEnum.TIKTOK,
	SocialMediaPlatformsEnum.YOUTUBE
];

const ANA_PANEL_INSET_VARIABLE = '--ana-panel-inset';

@Component({
	standalone: true,
	selector: 'app-ana-assistant-panel',
	changeDetection: ChangeDetectionStrategy.Default,
	imports: [
		CommonModule,
		AnaChatHostComponent,
		AnaCampaignStartComponent,
		IconComponent,
		TypographyComponent,
		LoButton,
		LoIcon,
		LoTooltip,
		DiscoveryProfileSlider,
		AnaSearchProgressComponent,
		AnaMarketplaceSearchWatchComponent,
		AnaFinderSearchWatchComponent
	],
	template: `
		<div
			class="resize-handle"
			*ngIf="!(facade.expanded$ | async)"
			[class.resize-handle--active]="resizing"
			(pointerdown)="onResizeStart($event)"
			(pointermove)="onResizeMove($event)"
			(pointerup)="onResizeEnd($event)"
			(pointercancel)="onResizeEnd($event)"
			(dblclick)="facade.toggleWidth()"
			loTooltip="Drag to resize · double-click to toggle width"
			loTooltipCompact
		></div>
		<div class="panel-header">
			<div class="panel-header__brand">
				<app-icon type="svg" [size]="16">ai_lolly_logo</app-icon>
				<typography variant="p/sm_semibold" class="panel-header__title">
					Ask Ana
				</typography>
			</div>
			<div class="panel-header__actions">
				<!--
					Two groups, because these are two kinds of control and the row
					used to make no distinction: four identical grey glyphs, evenly
					spaced, of which the first two act on the CONVERSATION and the
					last two act on the WINDOW. Held apart, the eye does not have
					to work out which is which — and "New chat", the one people
					press most, finally gets said in words instead of being a bare
					plus a reader has to interpret.

					Expanded, both conversation controls move into the rail (it
					already lists the conversations and carries its own New chat),
					so the group disappears and only the window controls remain.
				-->
				<div
					class="panel-header__group"
					*ngIf="!(facade.expanded$ | async)"
				>
					<button
						lo-button
						variant="ghost"
						size="sm"
						type="button"
						aria-label="New chat"
						(click)="facade.newChat()"
					>
						<lo-icon name="chrome_new_chat"></lo-icon>
						New chat
					</button>
					<button
						lo-button
						variant="ghost"
						size="sm"
						iconOnly
						type="button"
						loTooltip="History"
						loTooltipCompact
						aria-label="History"
						(click)="onHistoryClick()"
					>
						<lo-icon name="chrome_history"></lo-icon>
					</button>
				</div>

				<!--
					Gated on the group it separates. Expanded, both conversation
					controls move into the rail and the hairline would be left
					fencing off nothing — which is the exact fault it was added
					to fix in the shell header. It also carries __expand, so it
					goes with the expand button below 1280px, where the panel is
					already full-width and there is nothing to expand.
				-->
				<span
					class="panel-header__divider panel-header__expand"
					*ngIf="!(facade.expanded$ | async)"
					aria-hidden="true"
				></span>

				<!--
					TRAP: the tooltip is muted from the press until the pointer leaves.
					Expanding re-lays the header out and the hover state survives it, so a
					tooltip closed only by the press was re-shown as Collapse over the pane tabs.
				-->
				<button
					lo-button
					variant="ghost"
					size="sm"
					iconOnly
					class="panel-header__expand"
					type="button"
					aria-label="Toggle full width"
					[loTooltip]="
						(facade.expanded$ | async) ? 'Collapse' : 'Expand'
					"
					loTooltipCompact
					[loTooltipDisabled]="widthTooltipMuted"
					(mouseleave)="widthTooltipMuted = false"
					(click)="widthTooltipMuted = true; facade.toggleExpand()"
				>
					<lo-icon
						*ngIf="!(facade.expanded$ | async)"
						name="chrome_expand"
					></lo-icon>
					<lo-icon
						*ngIf="facade.expanded$ | async"
						name="chrome_collapse"
					></lo-icon>
				</button>
				<button
					lo-button
					variant="ghost"
					size="sm"
					iconOnly
					type="button"
					loTooltip="Minimize"
					loTooltipCompact
					aria-label="Minimize assistant"
					(click)="facade.hide()"
				>
					<!-- A minus, not a cross: hiding keeps the conversation and the page composer brings it back, so it reads as minimise rather than close. -->
					<lo-icon name="chrome_minimize"></lo-icon>
				</button>
			</div>
		</div>

		<app-ana-chat-host
			#host
			class="panel-assistant"
			holder="panel"
			[showHeader]="false"
			[welcomeSuggestionsEnabled]="true"
			[expanded]="(facade.expanded$ | async) === true"
			[railCollapsed]="(facade.railCollapsed$ | async) === true"
			[detailTemplate]="creatorDetail"
			[startTemplate]="
				(facade.campaignStart$ | async) ? campaignStart : null
			"
			[tallComposer]="(facade.campaignFlow$ | async) === true"
			(detailEntityChanged)="onDetailEntityChanged($event)"
			(railCollapsedChange)="facade.setRailCollapsed($event)"
		></app-ana-chat-host>

		<!--
			Create New Campaign's start screen, drawn in place of the greeting
			while the facade holds the flow. Continue sends the text as the
			first message, which moves the column to the chat.
		-->
		<ng-template #campaignStart>
			<app-ana-campaign-start
				[start]="facade.campaignStart$ | async"
				[compact]="!(facade.expanded$ | async)"
				(submitted)="facade.submitCampaignStart($event)"
			></app-ana-campaign-start>
		</ng-template>

		<!--
			What the expanded workspace's right-hand pane shows for a creator: the
			SAME profile the narrow panel opens as a slider, embedded. It lives here
			rather than in the library because the profile is app-level (discovery
			services, brand list, deep-dive chat), and the pane renders this template
			lazily — so the profile's metered request follows the reader's click and
			not the panel's bootstrap.
		-->
		<ng-template #creatorDetail>
			<app-post-details-slider
				*ngIf="creatorProfile"
				class="embedded"
				[embeddedData]="creatorProfile"
				(closeRequested)="host.assistant?.closeDetail()"
			></app-post-details-slider>
			<!--
				No profile to embed, and the pane hides its own header strip whenever
				this template is what renders — so the way out has to live here, or the
				column becomes a dead end.
			-->
			<div class="detail-unavailable" *ngIf="!creatorProfile">
				<button
					lo-button
					variant="ghost"
					size="sm"
					iconOnly
					round
					class="detail-unavailable__close"
					type="button"
					loTooltip="Close details"
					loTooltipCompact
					aria-label="Close details"
					(click)="host.assistant?.closeDetail()"
				>
					<lo-icon name="clear"></lo-icon>
				</button>
				This creator came from the conversation without a platform, so
				the full profile can't be opened here.
			</div>
		</ng-template>

		<!--
			What the pane shows for a project: what its search is doing, and who it
			has found so far. Two blocks in one column because they are two halves
			of one question — a roster of three is a failure at minute one and a
			result at minute six, and only the stages above it say which. Ten
			creators with avatars, follower counts and match rates do not fit a chat
			bubble either, so Ana's account of what she found stays in the
			conversation and the list itself lives here. Reopening it is the project
			chip in that same message — the pane is filled by pointing at an entity,
			so nothing extra is needed to get back to it.

			The progress block names the running job and the roster follows it, which
			is what makes a roster beside a live search keep up instead of reading
			once. It also carries the column's only close: the pane draws no header
			of its own for a hosted view, and a second X halfway down would be one
			too many.

			Rendered lazily by the pane, like the profile above, so both polls start
			on the reader's click rather than on the panel's bootstrap.
		-->
		<!--
			The watcher that outlives the pane.
			
			The visible progress lives in the project template below, and it dies
			with the pane the moment the reader opens a creator — taking the poll,
			and with it Ana's promise to come back, down with it. This one is
			mounted while a search is being waited on and the pane is showing
			something ELSE, so there is never a second poll on the same search:
			exactly one of the two is alive at a time.
		-->
		<app-ana-search-progress
			*ngIf="hiddenWatchProjectId"
			class="offscreen-watcher"
			[projectId]="hiddenWatchProjectId"
			(searchEnded)="onSearchEnded($event, hiddenWatchProjectId)"
		></app-ana-search-progress>
		<!--
			The same watcher for a Partner Ads Marketplace search: it draws
			nothing and only says when the search ended. At most one of the two
			is mounted, since the facade waits on one search at a time.
		-->
		<app-ana-marketplace-search-watch
			*ngIf="watchedMarketplaceSearchId"
			[searchId]="watchedMarketplaceSearchId"
			(searchEnded)="
				onMarketplaceSearchEnded($event, watchedMarketplaceSearchId)
			"
			(searchChecking)="
				onMarketplaceSearchChecking($event, watchedMarketplaceSearchId)
			"
		></app-ana-marketplace-search-watch>
		<!--
			And for an Affiliate Finder run: the same shape, reading the run's
			results. Still at most one of the three at a time.
		-->
		<app-ana-finder-search-watch
			*ngIf="watchedFinderRunId"
			[runId]="watchedFinderRunId"
			(searchEnded)="onFinderSearchEnded($event, watchedFinderRunId)"
		></app-ana-finder-search-watch>

		<!--
			There is no project view in this pane any more.
			
			It used to hold the search screen — stages, a raw creator list, an
			"Open project" button — as a way of showing that a search was
			running. That is the project's own surface in its own visual
			language, and in this pane it competed with the two things Ana makes
			and the reader saw them in an order that did not match the order they
			were made in. The working line in the conversation says a search is
			running; the pane belongs to the Creator plan and the Campaign review
			alone.
		-->
	`,
	styles: [
		`
			@use 'chrome-controls' as chrome;

			// Alive, not seen. It exists to keep the poll running while the pane
			// shows something else, and it must take no space and catch no
			// clicks doing it. Not display:none — a hidden subtree is still
			// constructed and its timers still run, which is the whole point,
			// but some browsers skip work inside it; taken out of flow instead.
			.offscreen-watcher {
				position: absolute;
				width: 0;
				height: 0;
				overflow: hidden;
				opacity: 0;
				pointer-events: none;
			}

			:host {
				display: flex;
				flex-direction: column;
				width: 100%;
				height: 100%;
				min-height: 0;
				background: #ffffff;
				border: 1px solid #dbdbdb;
				border-top-left-radius: 16px;
				overflow: hidden;
				position: relative;
			}

			.resize-handle {
				position: absolute;
				// Skip the top-left rounded corner (16px radius) so the
				// handle never sits over the rounded edge — and shrink the
				// hit area to 5px so the panel content doesn't lose clicks
				// to the resize zone on its leftmost pixels.
				left: 0;
				top: 16px;
				bottom: 0;
				width: 5px;
				cursor: ew-resize;
				z-index: 20;
				touch-action: none;

				&::after {
					content: '';
					position: absolute;
					left: 1.5px;
					top: 50%;
					transform: translateY(-50%);
					width: 2px;
					height: 36px;
					border-radius: 1px;
					background: linear-gradient(
						180deg,
						#3a1fea 0%,
						#a11af5 50%,
						#00b1ff 100%
					);
					opacity: 0;
					transition: opacity 200ms ease, height 200ms ease;
				}

				&:hover::after {
					opacity: 0.45;
				}

				&--active::after {
					opacity: 0.7;
					height: 48px;
				}

				// At the mobile breakpoint the host panel switches to
				// position:fixed and the resize semantics don't apply
				// (panel overlays content from the right). Hide the handle
				// so it doesn't sit on top of the chat content there.
				@media (max-width: 1279px) {
					display: none;
				}
			}

			.panel-header {
				display: flex;
				align-items: center;
				justify-content: space-between;
				padding: 1.25rem 1rem;
				flex-shrink: 0;
				// The bar spans all three columns of the expanded workspace and
				// had no line saying so, so the rail, the dialogue and the
				// inspector each appeared to start at their own top. Same
				// hairline the prototype's top bar carries.
				border-bottom: 1px solid #e4e7ec;
				// Hold layout width steady so the show/hide animation
				// just reveals/clips the panel — text inside never reflows.
				min-width: var(--ana-content-width, 364px);

				&__brand {
					display: flex;
					align-items: center;
					column-gap: 0.5rem;
				}

				// Solid, not a gradient clipped to the letters. The same two
				// words appear in the shell header in flat blue, so the panel
				// was drawing the product's own name a second way six inches
				// away — and a blue-to-cyan wash across 14px type is the least
				// legible of the two. Ana's colour lives in the mark beside it,
				// which is a drawing and can carry it.
				&__title {
					font-family: 'Gilroy', sans-serif;
					font-weight: 600;
					font-size: 14px;
					line-height: 20px;
					color: chrome.$chrome-ink-strong;
					-webkit-text-fill-color: currentColor;
				}

				&__actions {
					display: flex;
					align-items: center;
					column-gap: 0.25rem;
				}

				&__group {
					@include chrome.chrome-group;
					margin-right: 0.125rem;
				}

				&__divider {
					@include chrome.chrome-divider(1rem);
					margin: 0 0.25rem;
				}

				&__expand {
					// Desktop-only control: below 1280px the panel is already a
					// full-width overlay, so there is nothing to expand/collapse.
					@media (max-width: 1279px) {
						display: none;
					}
				}
			}

			.panel-assistant {
				flex: 1;
				min-height: 0;
				display: flex;
				min-width: var(--ana-content-width, 364px);
			}

			:host ::ng-deep app-ai-assistant {
				flex: 1;
				min-height: 0;
				border: none !important;
				border-radius: 0 !important;
			}

			.detail-unavailable {
				position: relative;
				padding: 1.5rem 3rem 1.5rem 1.25rem;
				font-family: 'Gilroy', sans-serif;
				font-size: 0.8125rem;
				font-weight: 500;
				line-height: 1.5;
				color: rgba(0, 0, 0, 0.45);

				&__close {
					position: absolute;
					right: 1rem;
					top: 1rem;
				}
			}
		`
	]
})
export class AnaAssistantPanelComponent implements AfterViewInit, OnDestroy {
	@ViewChild('host') private host!: AnaChatHostComponent;

	/** The chat component the host mounts — what the facade's helpers take. */
	get assistant(): AiAssistantComponent | undefined {
		return this.host?.assistant;
	}

	resizing = false;

	widthTooltipMuted = false;

	/**
	 * What the embedded profile in the detail pane is pointed at, or null when the
	 * pane holds nothing a profile can be loaded for (no entity, a project, or a
	 * creator the conversation carries without a platform).
	 *
	 * Derived once here from `detailEntityChanged` rather than computed inside the
	 * template: the whole assistant chain runs default change detection, so a
	 * method call in the binding would rebuild this object — and re-point the
	 * profile — on every pass.
	 */
	creatorProfile: DiscoveryProfileSliderData | null = null;

	/**
	 * The search being waited on, watched offscreen.
	 *
	 * It used to be "the searched project when the pane is NOT showing it",
	 * because the pane could show the project's own search screen. It no longer
	 * can, so the watcher is simply alive whenever a search is running: it has
	 * nothing to collide with and nothing to duplicate.
	 */
	hiddenWatchProjectId: string | null = null;
	private watchedSearchProjectId: string | null = null;
	/** The Marketplace search being waited on, watched offscreen. */
	watchedMarketplaceSearchId: string | null = null;
	/** The Affiliate Finder run being waited on, watched offscreen. */
	watchedFinderRunId: string | null = null;
	private readonly destroy$ = new Subject<void>();

	private refreshHiddenWatcher(): void {
		this.hiddenWatchProjectId = this.watchedSearchProjectId;
	}

	private resizeStartX = 0;
	private resizeStartWidth = 0;
	private resizePendingWidth: number | null = null;
	private resizeFrameScheduled = false;
	private capturedPointerId: number | null = null;
	private capturedTarget: HTMLElement | null = null;

	constructor(
		public facade: AnaAssistantFacadeService,
		private router: Router
	) {}

	ngAfterViewInit(): void {
		// Binding is the host's job now — it is the one place the slot is claimed,
		// and claiming it here as well would be a second binder by another name.
		// Which search this conversation is waiting on. The pane may or may not
		// be showing it; the hidden watcher covers the case where it is not.
		this.facade.watchedSearch
			.pipe(takeUntil(this.destroy$))
			.subscribe(search => {
				this.watchedSearchProjectId =
					search?.kind === 'ai_search' ? search.id : null;
				this.watchedMarketplaceSearchId =
					search?.kind === 'marketplace' ? search.id : null;
				this.watchedFinderRunId =
					search?.kind === 'finder' ? search.id : null;
				this.refreshHiddenWatcher();
			});
		this.publishInset();
	}

	/**
	 * How much of the window's right edge the open panel takes — read by the
	 * toast host so an Undo toast does not sit on the panel's header.
	 */
	private publishInset(): void {
		// CONTRACT: on the ROOT element, not the shell — the toast host is a child of app-root, outside the shell's container.
		combineLatest([
			this.facade.visible$,
			this.facade.width$,
			this.facade.expanded$
		])
			.pipe(takeUntil(this.destroy$))
			.subscribe(([visible, width, expanded]) => {
				// Fullscreen covers the whole window; there is no side to step to.
				const inset = visible && !expanded ? width + 'px' : '0px';
				document.documentElement.style.setProperty(
					ANA_PANEL_INSET_VARIABLE,
					inset
				);
			});
	}

	ngOnDestroy(): void {
		document.documentElement.style.removeProperty(ANA_PANEL_INSET_VARIABLE);
		this.restoreBodyDuringDrag(false);
		this.destroy$.next();
		this.destroy$.complete();
	}

	onHistoryClick(): void {
		this.assistant?.openHistory();
	}

	/**
	 * The pane's entity changed: prepare (or drop) the data each hosted view
	 * needs. A creator points the embedded profile, a project points the roster,
	 * and anything else leaves both empty.
	 *
	 * The platform is required, not defaulted: guessing Instagram for a TikTok
	 * creator loads a different account's profile under this creator's name.
	 */
	/**
	 * The search this pane was watching has stopped.
	 *
	 * Handed straight to the facade, which owns whether this conversation was
	 * waiting on it — the pane watches whatever project it is pointed at, and
	 * a project the reader opened out of curiosity must not start a turn.
	 */
	onSearchEnded(
		state: AiSearchProgressState,
		projectId: string | null
	): void {
		if (!projectId || !this.assistant) return;
		// The whole state, not the count. A poll that could not REACH the search
		// reports zero found, and handing over the number alone made losing
		// sight of a search indistinguishable from a search that found nobody.
		this.facade.onSearchEnded(this.assistant, projectId, state);
	}

	/** The Marketplace search the hidden watcher followed has stopped. */
	onMarketplaceSearchEnded(
		end: AnaMarketplaceSearchEnd,
		searchId: string | null
	): void {
		if (!searchId || !this.assistant) return;
		this.facade.onMarketplaceSearchEnded(this.assistant, searchId, end);
	}

	/** The Marketplace search is checking views and fit for `count` creators. */
	onMarketplaceSearchChecking(count: number, searchId: string | null): void {
		if (!searchId || !this.assistant) return;
		this.facade.onMarketplaceSearchChecking(
			this.assistant,
			searchId,
			count
		);
	}

	/** The Finder run the hidden watcher followed has settled, or was lost. */
	onFinderSearchEnded(end: AnaFinderSearchEnd, runId: string | null): void {
		if (!runId || !this.assistant) return;
		this.facade.onFinderSearchEnded(this.assistant, runId, end);
	}

	onDetailEntityChanged(entity: AnaEntityReference | null): void {
		if (
			!entity ||
			entity.kind !== 'creator' ||
			!entity.platform ||
			!DISCOVERY_PLATFORMS.includes(entity.platform)
		) {
			this.creatorProfile = null;
			return;
		}
		const influencerId = Number(entity.id);
		if (!Number.isInteger(influencerId) || influencerId <= 0) {
			this.creatorProfile = null;
			return;
		}
		this.creatorProfile = {
			influencerId,
			platform: entity.platform as SocialMediaPlatformsEnum
		};
	}

	onResizeStart(event: PointerEvent): void {
		event.preventDefault();
		const target = event.target as HTMLElement;
		target.setPointerCapture(event.pointerId);
		this.capturedPointerId = event.pointerId;
		this.capturedTarget = target;
		this.resizeStartX = event.clientX;
		this.resizeStartWidth = this.facade.width$.value;
		this.resizing = true;
		this.facade.resizing$.next(true);
		this.restoreBodyDuringDrag(true);
	}

	onResizeMove(event: PointerEvent): void {
		if (!this.resizing) return;
		// Panel is anchored to the right edge — dragging left widens it.
		const deltaX = this.resizeStartX - event.clientX;
		this.resizePendingWidth = this.resizeStartWidth + deltaX;
		if (this.resizeFrameScheduled) return;
		this.resizeFrameScheduled = true;
		// Coalesce pointermove updates into one width$ emission per animation
		// frame. Without this, every pixel of drag triggered an emit, an
		// async-pipe pass on 5 host bindings, and a synchronous
		// localStorage.setItem call (now deferred to pointerup).
		requestAnimationFrame(() => this.flushResizeFrame());
	}

	private flushResizeFrame(): void {
		this.resizeFrameScheduled = false;
		if (!this.resizing || this.resizePendingWidth === null) return;
		this.facade.setWidthLive(this.resizePendingWidth);
		this.resizePendingWidth = null;
	}

	onResizeEnd(event: PointerEvent): void {
		this.finishResize(event);
	}

	// Safety nets: if the handle is removed mid-drag, the pointer leaves the
	// window, or the OS steals focus, the pointerup/pointercancel bound on the
	// handle is missed and `resizing` would stick to true forever. These
	// window-level listeners guarantee cleanup.
	@HostListener('window:pointerup', ['$event'])
	onWindowPointerUp(event: PointerEvent): void {
		if (this.resizing) this.finishResize(event);
	}

	@HostListener('window:pointercancel', ['$event'])
	onWindowPointerCancel(event: PointerEvent): void {
		if (this.resizing) this.finishResize(event);
	}

	private finishResize(event: PointerEvent): void {
		if (!this.resizing) return;
		// Apply any buffered final width before flipping `resizing` off so the
		// last pointermove isn't lost when the user releases on the same frame.
		if (this.resizePendingWidth !== null) {
			this.facade.setWidthLive(this.resizePendingWidth);
			this.resizePendingWidth = null;
		}
		this.resizing = false;
		this.facade.resizing$.next(false);
		// Single persist call at the end of the drag — disk I/O batched.
		this.facade.setWidth(this.facade.width$.value);
		const target =
			(event.target as HTMLElement | null) ?? this.capturedTarget;
		const pointerId = this.capturedPointerId ?? event.pointerId;
		if (
			target &&
			pointerId !== null &&
			target.hasPointerCapture?.(pointerId)
		) {
			target.releasePointerCapture(pointerId);
		}
		this.capturedPointerId = null;
		this.capturedTarget = null;
		this.restoreBodyDuringDrag(false);
	}

	private restoreBodyDuringDrag(active: boolean): void {
		document.body.style.cursor = active ? 'ew-resize' : '';
		document.body.style.userSelect = active ? 'none' : '';
	}
}
